/**
 * parasutStatusCell.jsx — Muhasebe FAZ 4 (Tur 2 + Tur 3, 2026-09-29,
 * feature/parasut-fatura-faz4-web), tahsilat satırının Paraşüt fatura durum
 * rozeti + aksiyonları. `columnDefs.js` (masaüstü `action` kolonunun yanında
 * yeni bir kolon) VE `DebtLedgerTable.jsx` (`MobileLedgerCards`) TARAFINDAN
 * PAYLAŞILIR — tek kaynak, iki render yeri.
 *
 * Veri kaynağı: `DebtLedgerSubTab.jsx`'in TOPLU çektiği (satır başına istek
 * YOK) `usePaymentsInvoiceStatusBulk` sonucu — bu bileşen kendi sorgusunu
 * ATMAZ, `statusInfo` prop'u olarak alır (bkz. api/admin/parasutMapping.js).
 *
 * `statusInfo` yoksa VEYA `statusInfo.found === false` (bu tahsilat için
 * hiç Paraşüt outbox kaydı yok — PARASUT_ENABLED kapalıyken veya Faz 4
 * öncesi eski kayıtlarda beklenen durum) → sessizce `—` (Dash), gürültü
 * yapılmaz.
 *
 * Tur 3 (koordinatör backend-teyit turu) — Tur 2'nin "outbox_id yok" boşluğu
 * KAPANDI: `_invoice_status_summary()` (routes/parasut_admin.py, salt
 * okundu) artık `outbox_id` döndürüyor. `failed` durumundaki bir satırda
 * artık BURADAN doğrudan `POST /admin/parasut/outbox/{outbox_id}/retry`
 * çağrılabiliyor (not zorunlu, `useRetryOutboxItem`). "Outbox'a git" linki
 * YEDEK olarak kalır — admin daha geniş bağlamda (diğer failed/needs_
 * verification/unknown satırlarla birlikte) incelemek isterse. `outbox_id`
 * (eski kayıtlarda teorik olarak) boşsa retry butonu GÖSTERİLMEZ, yalnızca
 * link kalır (savunma amaçlı, uydurma davranış yok).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, RotateCcw } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import StatusBadge from '@/components/admin/StatusBadge';
import { useToast } from '@/contexts/ToastContext';
import { getApiErrorMessage } from '@/utils/apiError';
import { useMarkPaymentRefunded, useRetryOutboxItem } from '@/api/admin/parasutMapping';

// ── Durum -> rozet eşlemesi ────────────────────────────────────────────────
// Tur 6 (elle test bulguları) — `missing_product_mapping`/`missing_account_
// mapping` ARTIK AYRI etiketler taşır (önceden tek "Ürün/hesap eşlemesi
// eksik" metnine birleştirilmişti) — hangi eşlemenin eksik olduğu admin'e
// doğrudan söylenir. `depends_on` ve tanınmayan/`null` bir `block_reason`
// AYNI "Önceki adımı bekliyor" metnine düşer (AdminParasutMapping.jsx'teki
// `blockReason.depends_on` İLE AYNI terim — tutarlılık).

const _BLOCKED_REASON_KEY = {
  missing_tax_number: 'accounting.parasut_status.missing_tax_number',
  missing_product_mapping: 'accounting.parasut_status.missing_product_mapping',
  missing_account_mapping: 'accounting.parasut_status.missing_account_mapping',
  depends_on: 'accounting.parasut_status.blocked_waiting',
};

function resolveBadge(statusInfo, t) {
  const status = statusInfo?.status;
  if (status === 'pending') {
    return { variant: 'warning', label: t('accounting.parasut_status.pending') };
  }
  if (status === 'in_progress') {
    return { variant: 'warning', label: t('accounting.parasut_status.in_progress') };
  }
  if (status === 'awaiting_job') {
    return { variant: 'warning', label: t('accounting.parasut_status.awaiting_job') };
  }
  if (status === 'done') {
    return { variant: 'success', label: t('accounting.parasut_status.done') };
  }
  if (status === 'failed') {
    return { variant: 'danger', label: t('accounting.parasut_status.failed') };
  }
  if (status === 'blocked') {
    const reason = statusInfo?.block_reason;
    const key = _BLOCKED_REASON_KEY[reason] || 'accounting.parasut_status.blocked_waiting';
    // "Bekliyor" (depends_on/tanınmayan sebep) amber — eylem GEREKMEZ, kendi
    // kendine çözülür. Eksik eşleme/TC ise kırmızı — admin AKSİYON almalı.
    const isWaiting = !reason || reason === 'depends_on';
    return { variant: isWaiting ? 'warning' : 'danger', label: t(key) };
  }
  if (status === 'needs_verification') {
    return { variant: 'warning', label: t('accounting.parasut_status.needs_verification') };
  }
  if (status === 'unknown') {
    return { variant: 'neutral', label: t('accounting.parasut_status.unknown') };
  }
  if (status === 'cancelled') {
    return { variant: 'neutral', label: t('accounting.parasut_status.cancelled') };
  }
  return { variant: 'neutral', label: status || '—' };
}

/** Yalnızca rozet — popover/aksiyon yok (kompakt gösterim gereken yerler için). */
export function ParasutStatusBadge({ statusInfo, t }) {
  if (!statusInfo?.found) return <span className="text-[var(--text-muted)]">—</span>;
  const { variant, label } = resolveBadge(statusInfo, t);
  return <StatusBadge variant={variant} label={label} />;
}

/**
 * Tam hücre: rozet + (varsa) "manuel iade faturası" uyarısı/aksiyonu +
 * (failed ise) doğrudan "Tekrar Gönder" + Outbox'a yönlendirme linki.
 * `Popover` içine alınır ki dar tablo hücresinde detay/aksiyon alanı taşmasın.
 */
export function ParasutStatusCell({ paymentId, statusInfo, t }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [refundNote, setRefundNote] = useState('');
  const [refundSubmitted, setRefundSubmitted] = useState(false);
  const [retryNote, setRetryNote] = useState('');
  const [retrySubmitted, setRetrySubmitted] = useState(false);
  const markRefunded = useMarkPaymentRefunded();
  const retryOutbox = useRetryOutboxItem();

  if (!statusInfo?.found) return <span className="text-[var(--text-muted)]">—</span>;

  const { variant, label } = resolveBadge(statusInfo, t);
  const isFailed = statusInfo.status === 'failed';
  const canRetryDirectly = isFailed && !!statusInfo.outbox_id;
  const needsRefundMark = !!statusInfo.manual_credit_note_required && !statusInfo.refunded_marked;
  const hasDetail = isFailed || needsRefundMark || statusInfo.refunded_marked;

  const submitMarkRefunded = async () => {
    setRefundSubmitted(true);
    const trimmed = refundNote.trim();
    if (!trimmed) return;
    try {
      await markRefunded.mutateAsync({ paymentId, note: trimmed });
      toast.success(t('accounting.parasut_status.mark_refunded_success'));
      setOpen(false);
      setRefundNote('');
      setRefundSubmitted(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const submitRetry = async () => {
    setRetrySubmitted(true);
    const trimmed = retryNote.trim();
    if (!trimmed) return;
    try {
      await retryOutbox.mutateAsync({ outboxId: statusInfo.outbox_id, note: trimmed });
      toast.success(t('accounting.parasut_status.retry_success'));
      setOpen(false);
      setRetryNote('');
      setRetrySubmitted(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const badge = (
    <span className="inline-flex items-center gap-1">
      <StatusBadge variant={variant} label={label} />
      {statusInfo.refunded_marked && (
        <StatusBadge variant="neutral" label={t('accounting.parasut_status.refunded_badge')} />
      )}
    </span>
  );

  if (!hasDetail) return badge;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex" data-testid={`parasut-status-trigger-${paymentId}`}>
          {badge}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-2 bg-white text-xs">
        {isFailed && (
          <div className="space-y-1.5">
            <p className="flex items-start gap-1.5 text-rose-700 font-semibold">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              {t('accounting.parasut_status.failed_hint')}
            </p>

            {canRetryDirectly && (
              <>
                <label htmlFor={`parasut-retry-note-${paymentId}`} className="block font-bold">
                  {t('accounting.parasut_status.retry_note_label')}
                </label>
                <textarea
                  id={`parasut-retry-note-${paymentId}`}
                  rows={2}
                  value={retryNote}
                  onChange={(e) => { setRetryNote(e.target.value); setRetrySubmitted(false); }}
                  className="w-full px-2 py-1.5 rounded-lg border-2 border-[var(--border-light)] text-xs"
                  data-testid={`parasut-retry-note-input-${paymentId}`}
                />
                {retrySubmitted && !retryNote.trim() && (
                  <p role="alert" className="text-rose-600 font-bold">
                    {t('accounting.parasut_status.retry_note_required')}
                  </p>
                )}
                <button
                  type="button"
                  onClick={submitRetry}
                  disabled={retryOutbox.isPending}
                  className="w-full py-1.5 rounded-lg bg-rose-500 text-white text-xs font-bold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                  data-testid={`parasut-retry-btn-${paymentId}`}
                >
                  <RotateCcw size={12} />
                  {retryOutbox.isPending ? t('accounting.parasut_status.retry_pending') : t('accounting.parasut_status.retry_button')}
                </button>
              </>
            )}

            <Link
              to="/admin/parasut-mapping?tab=outbox"
              className="inline-flex items-center gap-1 font-bold text-[var(--brand-primary)] hover:underline"
              data-testid={`parasut-status-outbox-link-${paymentId}`}
            >
              {t('accounting.parasut_status.go_to_outbox')}
              <ExternalLink size={11} />
            </Link>
          </div>
        )}

        {needsRefundMark && (
          <div className="space-y-1.5 border-t border-[var(--border-light)] pt-2 first:border-t-0 first:pt-0">
            <p className="flex items-start gap-1.5 text-amber-700 font-semibold">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              {t('accounting.parasut_status.manual_credit_note_warning')}
            </p>
            <label htmlFor={`parasut-refund-note-${paymentId}`} className="block font-bold">
              {t('accounting.parasut_status.mark_refunded_note_label')}
            </label>
            <textarea
              id={`parasut-refund-note-${paymentId}`}
              rows={2}
              value={refundNote}
              onChange={(e) => { setRefundNote(e.target.value); setRefundSubmitted(false); }}
              className="w-full px-2 py-1.5 rounded-lg border-2 border-[var(--border-light)] text-xs"
              data-testid={`parasut-refund-note-input-${paymentId}`}
            />
            {refundSubmitted && !refundNote.trim() && (
              <p role="alert" className="text-rose-600 font-bold">
                {t('accounting.parasut_status.mark_refunded_note_required')}
              </p>
            )}
            <button
              type="button"
              onClick={submitMarkRefunded}
              disabled={markRefunded.isPending}
              className="w-full py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold disabled:opacity-50"
              data-testid={`parasut-mark-refunded-btn-${paymentId}`}
            >
              {markRefunded.isPending ? t('accounting.parasut_status.mark_refunded_pending') : t('accounting.parasut_status.mark_refunded_button')}
            </button>
          </div>
        )}

        {statusInfo.refunded_marked && (
          <div className="border-t border-[var(--border-light)] pt-2 first:border-t-0 first:pt-0">
            <p className="font-bold text-[var(--text-main)]">{t('accounting.parasut_status.refunded_badge')}</p>
            {statusInfo.refunded_note && <p className="text-[var(--text-muted)] mt-0.5">{statusInfo.refunded_note}</p>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
